import React from 'react';
import { useQuery } from 'react-query';
import api from '../../../services/api';
import { useState, useEffect } from 'react';
import {
  UsersIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

// Components
import StatCard from './StatCards';
import RevenueChart from './RevenueChart';
import RecentBookings from './RecentBookings';

const DashboardPage = () => {
  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery('dashboardStats', async () => {
    const response = await api.get('/api/v1/admin/dashboard/stats');
    return response.data.data;
  }, {
    onError: (error) => {
      console.error('Error fetching dashboard stats:', error);
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Use placeholder data if API fails
    placeholderData: {
      totalBookings: 0,
      totalCustomers: 0,
      totalRevenue: 0,
      totalVehicles: 0,
      recentBookings: []
    }
  });
  
  // Fetch revenue chart data
  const { data: revenueData, isLoading: revenueLoading } = useQuery('dashboardRevenue', async () => {
    const response = await api.get('/api/v1/admin/dashboard/revenue');
    return response.data.data;
  }, {
    onError: (error) => {
      console.error('Error fetching revenue data:', error);
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Format revenue data for chart or use placeholder if not available
  const chartData = revenueData ? {
    labels: revenueData.months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.revenue || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
      },
      {
        label: 'Bookings',
        data: revenueData.bookings || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
      }
    ]
  } : {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
      },
      {
        label: 'Bookings',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
      }
    ]
  };

  // Get current date and time for last updated display
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update the current time when data is refreshed
  useEffect(() => {
    if (!statsLoading && !revenueLoading) {
      setCurrentTime(new Date());
    }
  }, [stats, revenueData, statsLoading, revenueLoading]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
        <div>
          <span className="text-sm text-gray-500">Last updated: {currentTime.toLocaleString()}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Total Bookings" 
          value={statsLoading ? '...' : stats?.totalBookings || 0} 
          icon={CalendarIcon} 
          color="bg-blue-500" 
        />
        <StatCard 
          title="Total Customers" 
          value={statsLoading ? '...' : stats?.totalCustomers || 0} 
          icon={UsersIcon} 
          color="bg-green-500" 
        />
        <StatCard 
          title="Total Revenue" 
          value={statsLoading ? '...' : `$${stats?.totalRevenue?.toLocaleString() || 0}`} 
          icon={CurrencyDollarIcon} 
          color="bg-yellow-500" 
        />
        <StatCard 
          title="Available Vehicles" 
          value={statsLoading ? '...' : stats?.totalVehicles || 0} 
          icon={TruckIcon} 
          color="bg-purple-500" 
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h2>
          {revenueLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <RevenueChart data={chartData} />
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Bookings</h2>
          <RecentBookings bookings={stats?.recentBookings || []} isLoading={statsLoading} />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
